import { cloneDeep, isEmpty } from 'lodash';
import {
  RECEIVED_SECURITY_OPERATIONAL_CONSENT,
  RECEIVED_ALERTS_AND_NOTIFICATIONS,
  UPDATE_ALERT_OPTIONS,
  UPDATE_ALERTS_AND_NOTIFICATIONS_PHONE,
  UPDATE_ALERTS_AND_NOTIFICATIONS_EMAIL,
  RECEIVED_ACTIVATED_FOR_TEXT_ALERTS,
  RECEIVED_MOBILE_CARRIERS,
  RECEIVED_SHORT_CODE_DEACTIVATION_REASON
} from 'state/alertsAndNotifications/AlertsAndNotificationsActions';
import {
  ALERTS_AND_NOTIFICATIONS_EMAIL,
  ALERTS_AND_NOTIFICATIONS_SMS,
  RETAIL_BUSINESS_LINE_ID,
  FAIL_REASON_CODE,
  NIGHTLY_DEACTIVATION_REASON_CODE,
  NO_TEXT_SENT_LIMIT_REASON_CODE
} from 'services/AlertsAndNotifications';
import {
  RECEIVED_CUSTOMER_PREFERENCES,
  RECEIVED_UPDATE_CUSTOMER_PREFERENCES
} from 'state/preferences/PreferencesActions';
import {
  appPhoneNumberToComponentPhoneNumberMapper,
  appEmailToComponentEmailMapper
} from 'services/Preferences';

export const alertsAndNotificationsSelectionsInitialState = {
  iverify: ALERTS_AND_NOTIFICATIONS_EMAIL,
  'contact-information': ALERTS_AND_NOTIFICATIONS_EMAIL,
  'mutual-fund-transactions': ALERTS_AND_NOTIFICATIONS_EMAIL,
  'account-information': ALERTS_AND_NOTIFICATIONS_EMAIL
};

export const alertsAndNotificationsReducer = (state = [], action) => {
  const { type } = action;

  if (type === RECEIVED_ALERTS_AND_NOTIFICATIONS) {
    const { alertsAndNotifications } = action;
    if (alertsAndNotifications) {
      return alertsAndNotifications;
    }
  }

  return state;
};

export const alertsAndNotificationsEmailReducer = (state = '', action) => {
  const { type } = action;

  if (
    type === RECEIVED_CUSTOMER_PREFERENCES ||
    type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES
  ) {
    const {
      preferences: { contactInformation }
    } = action;

    if (contactInformation) {
      const { primaryEmailAddress } = contactInformation;

      if (primaryEmailAddress) {
        const mappedEmail = appEmailToComponentEmailMapper(primaryEmailAddress);

        if (mappedEmail) {
          return mappedEmail;
        }
      }
    }
  }

  if (type === UPDATE_ALERTS_AND_NOTIFICATIONS_EMAIL) {
    const { email } = action;
    let copyState = cloneDeep(state);

    copyState['email'] = email;

    return copyState;
  }
  return state;
};

export const alertsAndNotificationsPhoneNumbersReducer = (
  state = '',
  action
) => {
  const { type } = action;

  if (
    type === RECEIVED_CUSTOMER_PREFERENCES ||
    type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES
  ) {
    const {
      preferences: { contactInformation }
    } = action;

    if (contactInformation) {
      const { phoneNumbers } = contactInformation;

      if (phoneNumbers) {
        const mappedPhoneNumbers =
          appPhoneNumberToComponentPhoneNumberMapper(phoneNumbers);
        if (mappedPhoneNumbers) {
          return mappedPhoneNumbers;
        }
      }
    }
  }

  if (type === UPDATE_ALERTS_AND_NOTIFICATIONS_PHONE) {
    const { phone } = action;
    let copyState = cloneDeep(state);

    copyState['mobilePhone'] = phone;

    return copyState;
  }

  return state;
};

export const alertsAndNotificationsSelectionsReducer = (
  state = alertsAndNotificationsSelectionsInitialState,
  action
) => {
  const { type } = action;

  if (type === UPDATE_ALERT_OPTIONS) {
    const { field, selection } = action;
    let clonedState = cloneDeep(state);

    if (field && selection) {
      clonedState[field] = selection;
      return clonedState;
    }
  }

  if (type === RECEIVED_ALERTS_AND_NOTIFICATIONS) {
    const { alertsAndNotifications } = action;
    let clonedState = cloneDeep(state);
    if (!isEmpty(alertsAndNotifications)) {
      const filteredAlerts = alertsAndNotifications.filter(
        (alert) => alert.businessLineId === RETAIL_BUSINESS_LINE_ID
      );
      filteredAlerts.forEach((alert) => {
        const { activityAlertCategoryType, contactMethods, businessLineId } =
          alert;

        if (
          !isEmpty(contactMethods) &&
          activityAlertCategoryType &&
          businessLineId
        ) {
          const hasSms = contactMethods.find((method) => {
            const { activityAlertContactMethodTypeId } = method;
            if (activityAlertContactMethodTypeId) {
              return (
                activityAlertContactMethodTypeId ===
                ALERTS_AND_NOTIFICATIONS_SMS
              );
            }
          });
          if (hasSms) {
            clonedState[activityAlertCategoryType] =
              ALERTS_AND_NOTIFICATIONS_SMS;
          } else {
            clonedState[activityAlertCategoryType] =
              ALERTS_AND_NOTIFICATIONS_EMAIL;
          }
        }
      });
      return clonedState;
    }
  }

  return state;
};

export const isActivatedForTextAlertsReducer = (state = false, action) => {
  const { type } = action;

  if (type === RECEIVED_ACTIVATED_FOR_TEXT_ALERTS) {
    const { isRegistered } = action;

    return isRegistered;
  }

  return state;
};

export const mobileCarriersReducer = (state = [], action) => {
  const { type } = action;

  if (type === RECEIVED_MOBILE_CARRIERS) {
    const { carriers } = action;

    if (!isEmpty(carriers)) {
      let transformedCarriers = carriers.map((carrier) => {
        const { mobileCarrierName, mobileCarrierId } = carrier;
        return {
          label: mobileCarrierName,
          value: mobileCarrierId
        };
      });

      return transformedCarriers;
    }

    return state;
  }

  return state;
};

export const hasTextMessageConsentReducer = (state = false, action) => {
  const { type } = action;

  if (type === RECEIVED_SECURITY_OPERATIONAL_CONSENT) {
    return action && action.consent ? action.consent : state;
  }

  return state;
};

export const hasOptedOutOfTextAlertsReducer = (state = false, action) => {
  const { type } = action;

  if (type === RECEIVED_ALERTS_AND_NOTIFICATIONS) {
    const { alertsAndNotifications } = action;
    if (!isEmpty(alertsAndNotifications)) {
      const filteredAlerts = alertsAndNotifications.filter(
        (alert) => alert.businessLineId === RETAIL_BUSINESS_LINE_ID
      );
      return filteredAlerts.some((alert) => alert.optedOut);
    }

    return state;
  }

  return state;
};

export const hasDeactivationReasonReducer = (state = false, action) => {
  const { type } = action;

  if (type === RECEIVED_SHORT_CODE_DEACTIVATION_REASON) {
    const { reason } = action;

    if (reason && reason.reasonCode) {
      const { reasonCode } = reason;
      return (
        reasonCode === FAIL_REASON_CODE ||
        reasonCode === NIGHTLY_DEACTIVATION_REASON_CODE ||
        reasonCode === NO_TEXT_SENT_LIMIT_REASON_CODE
      );
    }

    return state;
  }

  return state;
};
